/**
 * AppShell — shared application chrome for all authenticated areas.
 *
 * Both TenantLayout and AdminLayout compose from this module. The ONLY
 * differences between the two shells are:
 *   (a) the navigation groups they pass in,
 *   (b) the badge rendered next to the logo,
 *   (c) which topbar features are enabled (notifications, currency switcher),
 *   (d) which profile-dropdown items appear in the sidebar footer.
 *
 * Everything else — sidebar header/content/footer structure, topbar chrome,
 * rail/icon collapse, cookie-persisted open state, route-active highlighting,
 * auto-expand of the active group, keyboard shortcuts, skip-to-content — is
 * shared and lives here exactly once.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFormatters } from "../lib/formatters";
import { APP_VERSION } from "../lib/appVersion";
import { Bell, ChevronRight, Search } from "lucide-react";
import CurrencySwitcher from "./CurrencySwitcher";
import { UserMenu } from "./UserMenu";
import { AppBreadcrumb } from "./AppBreadcrumb";
import { AppLink } from "./AppLink";
import { RouteErrorBoundary } from "./ErrorBoundary";
import { useSidebarGroupsStore } from "../stores/sidebarGroupsStore";
import { SETTINGS_ROUTES } from "../lib/constants/settingsRoutes";
import {
  Sidebar,
  SIDEBAR_COOKIE_NAME,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import TopbarCommandPalette from "./TopbarCommandPalette";
import {
  useUnreadNotificationCount,
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  NotificationType,
  type NotificationItem,
} from "../services/notificationQueries";
import JobResultDrawer from "./JobResultDrawer";

// ── Shared types ──────────────────────────────────────────────────────────────

export interface NavItem {
  labelKey: string;
  path: string;
  icon: React.ReactNode;
}

export interface NavGroup {
  labelKey: string;
  items: NavItem[];
}

/** A single item that appears in the sidebar footer profile dropdown. */
export interface ProfileDropdownItem {
  labelKey: string;
  icon: React.ReactNode;
  onClick: () => void;
}

export interface AppShellBadge {
  /** i18n key or raw text for the badge next to the logo. */
  text: string;
  /** Tailwind class string for badge color (semantic tokens only). */
  className: string;
}

// ── Internal helpers ──────────────────────────────────────────────────────────

function slugifyKey(labelKey: string): string {
  return labelKey.replace(/\./g, "-");
}

function formatTimeAgo(
  iso: string,
  t: (k: string, opts?: Record<string, unknown>) => string,
  formatDate: (d: string) => string,
): string {
  const d = new Date(iso);
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return t("notification.justNow");
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return t("notification.minutesAgo", { n: diffMin });
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return t("notification.hoursAgo", { n: diffHour });
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) return t("notification.daysAgo", { n: diffDay });
  return formatDate(iso);
}

// ── AppShellSidebar ───────────────────────────────────────────────────────────

interface AppShellSidebarProps {
  navGroups: NavGroup[];
  badge: AppShellBadge;
  isActive: (path: string) => boolean;
  /** Called when a nav item is clicked. Caller decides the navigate impl. A `false` return means a
   *  registered leave-guard cancelled the navigation (`useViewTransitionNavigate`'s own contract). */
  navigate: (path: string) => boolean | void;
}

export const AppShellSidebar: React.FC<AppShellSidebarProps> = ({
  navGroups,
  badge,
  isActive,
  navigate,
}) => {
  const { t } = useTranslation();
  const { setOpenMobile, isMobile, state } = useSidebar();
  const collapsedGroups = useSidebarGroupsStore((s) => s.collapsedGroups);
  const toggleGroup = useSidebarGroupsStore((s) => s.toggleGroup);

  // Desktop rail (icon) modunda grup label'ları gizli — kullanıcının kapadığı
  // gruplar boş bir sidebar'a yol açar. Bu durumda item'ları her zaman göster;
  // manuel collapse sadece expanded modda anlamlı.
  const railMode = state === "collapsed" && !isMobile;

  const handleNavClick = useCallback(
    (path: string) => {
      // A guarded navigate returning `false` means the user cancelled at a leave-guard prompt —
      // the drawer must stay open too, not just the route.
      const proceeded = navigate(path);
      if (isMobile && proceeded !== false) setOpenMobile(false);
    },
    [navigate, isMobile, setOpenMobile],
  );

  return (
    <Sidebar
      collapsible="icon"
      className="border-r border-sidebar-border group/beetegre-sidebar"
    >
      <SidebarHeader className="border-b border-sidebar-border h-14 flex-row items-center gap-2 px-3">
        {/* Genişletilmiş mod: wordmark + rozet + trigger sağda.
            Dark tema logonun ink-mavi dolgularını okunmaz yapar; tema başına ayrı asset. */}
        <img
          src="/logo-wordmark.svg"
          alt="Beetegre"
          className="block h-7 w-auto shrink-0 dark:hidden group-data-[collapsible=icon]:hidden"
        />
        <img
          src="/logo-wordmark-dark.svg"
          alt="Beetegre"
          className="block h-7 w-auto shrink-0 not-dark:hidden group-data-[collapsible=icon]:hidden"
        />
        <span className={cn("font-mono text-[0.65rem] font-semibold px-1.5 py-0.5 rounded-full border group-data-[collapsible=icon]:hidden", badge.className)}>
          {badge.text}
        </span>
        <SidebarTrigger
          aria-label={t("sidebar.toggleSidebar")}
          className="ml-auto shrink-0 text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground group-data-[collapsible=icon]:hidden"
        />

        {/* Rail (icon) modu: hover'da logo yerine trigger görünür */}
        <div className="flex not-group-data-[collapsible=icon]:hidden relative w-full h-9 items-center justify-center">
          <img
            src="/logo-mark.svg"
            alt="Beetegre"
            className="absolute inset-0 m-auto h-7 w-auto dark:hidden transition-opacity duration-150 group-hover/beetegre-sidebar:opacity-0 pointer-events-none"
          />
          <img
            src="/logo-mark-dark.svg"
            alt="Beetegre"
            className="absolute inset-0 m-auto block h-7 w-auto not-dark:hidden transition-opacity duration-150 group-hover/beetegre-sidebar:opacity-0 pointer-events-none"
          />
          <SidebarTrigger
            aria-label={t("sidebar.toggleSidebar")}
            className="absolute inset-0 m-auto opacity-0 text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-opacity duration-150 group-hover/beetegre-sidebar:opacity-100 group-hover/beetegre-sidebar:pointer-events-auto pointer-events-none"
          />
        </div>
      </SidebarHeader>

      <SidebarContent className="py-2">
        {navGroups.map((group) => {
          const isOpen = railMode ? true : !collapsedGroups[group.labelKey];
          const slug = slugifyKey(group.labelKey);
          return (
            <Collapsible
              key={group.labelKey}
              open={isOpen}
              onOpenChange={() => {
                if (!railMode) toggleGroup(group.labelKey);
              }}
              className="group/collapsible"
            >
              <SidebarGroup className="py-0.5">
                <SidebarGroupLabel
                  asChild
                  className="group-data-[collapsible=icon]:hidden"
                >
                  <CollapsibleTrigger
                    aria-controls={`nav-group-${slug}`}
                    className="flex w-full items-center justify-between px-2 text-[0.65rem] font-semibold uppercase tracking-widest text-sidebar-foreground/50 hover:text-sidebar-foreground transition-colors"
                  >
                    <span>{t(group.labelKey)}</span>
                    <ChevronRight
                      size={12}
                      aria-hidden="true"
                      className="transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
                    />
                  </CollapsibleTrigger>
                </SidebarGroupLabel>
                <CollapsibleContent
                  id={`nav-group-${slug}`}
                  className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down"
                >
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {group.items.map((item) => (
                        <SidebarMenuItem key={item.path}>
                          {/* asChild + AppLink: gezinme öğesi gerçek bir bağlantı,
                              böylece sağ tık menüsü, orta tık ve Ctrl+tık çalışır. */}
                          <SidebarMenuButton
                            asChild
                            tooltip={t(item.labelKey)}
                            isActive={isActive(item.path)}
                          >
                            <AppLink to={item.path} onNavigate={handleNavClick}>
                              {item.icon}
                              <span>{t(item.labelKey)}</span>
                            </AppLink>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </SidebarGroup>
            </Collapsible>
          );
        })}
      </SidebarContent>

      {/* Footer artık yalnız sürüm damgası taşıyor: hesap menüsü topbar'ın sağ
          üstüne taşındı (dil ve tema tercihleri de onun içinde). */}
      <SidebarFooter className="border-t border-sidebar-border p-3">
        <span
          className="block px-1 font-mono text-[10px] text-muted-foreground group-data-[collapsible=icon]:hidden"
          title={t("sidebar.versionTitle", { version: APP_VERSION })}
        >
          {t("sidebar.version", { version: APP_VERSION })}
        </span>
      </SidebarFooter>
    </Sidebar>
  );
};

// ── AppShellTopbar ────────────────────────────────────────────────────────────

interface AppShellTopbarProps {
  navGroups: NavGroup[];
  /** Whether the notification bell should be shown. False for super-admin
   *  (notifications are tenant-scoped; no feed exists for the super-admin area). */
  showNotifications: boolean;
  /** Whether the CurrencySwitcher should be shown. False for super-admin. */
  showCurrencySwitcher: boolean;
  /** tenantId required when showNotifications is true. */
  tenantId?: string;
  /** Called when the command palette navigates to a page. */
  onNavigate: (path: string) => void;
  /** Called when a bulk-job notification is clicked.
   *  Only used when showNotifications is true. */
  onBulkJobOpen?: (jobId: string) => void;
  /** Account-menu entries shown above the tenant switcher. */
  profileDropdownItems: ProfileDropdownItem[];
  handleLogout: () => Promise<void>;
  loggingOut: boolean;
  logoutError: string | null;
}

export const AppShellTopbar = React.memo(function AppShellTopbar({
  navGroups,
  showNotifications,
  showCurrencySwitcher,
  tenantId,
  onNavigate,
  onBulkJobOpen,
  profileDropdownItems,
  handleLogout,
  loggingOut,
  logoutError,
}: AppShellTopbarProps) {
  const { t } = useTranslation();
  const { formatDate } = useFormatters();

  // ── Command palette ───────────────────────────────────────────────
  // Palet artık sayfanın ortasında modal olarak açılıyor, topbar'daki tetikleyiciye
  // demirlenmiş bir dropdown olarak değil. Tetikleyici de bu yüzden yalnız bir ikon
  // + kısayol rozeti: aramanın kendisi paletin içinde yaşıyor.
  const searchBtnRef = useRef<HTMLButtonElement | null>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const openPalette = useCallback(() => {
    setPaletteOpen(true);
  }, []);

  const closePalette = useCallback(() => {
    setPaletteOpen(false);
    searchBtnRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      const isInput =
        tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";

      if ((e.key === "k" || e.key === "K") && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        openPalette();
        return;
      }

      if (
        e.key === "/" &&
        !isInput &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey
      ) {
        e.preventDefault();
        openPalette();
        return;
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openPalette]);

  const handlePaletteNavigate = useCallback(
    (path: string) => {
      onNavigate(path);
    },
    [onNavigate],
  );

  // ── Notifications (tenant-only) ───────────────────────────────────
  const [notifOpen, setNotifOpen] = useState(false);

  const { data: notifUnread = 0 } = useUnreadNotificationCount(
    showNotifications ? tenantId : undefined,
  );

  const {
    data: notifItems = [],
    isLoading: notifLoading,
    isError: notifError,
  } = useNotifications(
    showNotifications ? tenantId : undefined,
    showNotifications ? notifOpen : false,
  );

  const markRead = useMarkNotificationRead(showNotifications ? (tenantId ?? "") : "");
  const markAllRead = useMarkAllNotificationsRead(showNotifications ? (tenantId ?? "") : "");

  const handleNotifClick = useCallback(
    (n: NotificationItem) => {
      if (!n.isRead) markRead.mutate(n.id);
      setNotifOpen(false);
      if (n.type === NotificationType.BulkJobCompleted && n.entityId) {
        onBulkJobOpen?.(n.entityId);
        return;
      }
      if (n.link) onNavigate(n.link);
    },
    [markRead, onNavigate, onBulkJobOpen],
  );

  const renderNotificationTitle = useCallback(
    (n: NotificationItem): string => {
      if (n.type === NotificationType.BulkJobCompleted) {
        const jobType = n.params?.jobType;
        const jobLabel = t(`job.bulk.type.${jobType}`, {
          defaultValue: jobType ?? "",
        });
        return t(n.titleKey, { ...n.params, jobLabel });
      }
      return t(n.titleKey, { ...(n.params ?? {}) });
    },
    [t],
  );

  const renderNotificationBody = useCallback(
    (n: NotificationItem): string | null => {
      if (!n.bodyKey) return null;
      return t(n.bodyKey, { ...(n.params ?? {}) });
    },
    [t],
  );

  const handleMarkAllRead = useCallback(() => {
    markAllRead.mutate();
  }, [markAllRead]);

  return (
    <div className="shrink-0 z-40 flex items-center h-14 px-4 md:px-8 bg-card border-b border-border gap-3">
      {/* Mobil drawer aç — desktop'ta trigger sidebar header'ında durur */}
      <SidebarTrigger
        className="shrink-0 md:hidden"
        aria-label={t("sidebar.toggleSidebar")}
      />

      {/* Konum göstergesi — arama kutusunun boşalttığı yer */}
      <AppBreadcrumb onNavigate={onNavigate} />

      <TopbarCommandPalette
        open={paletteOpen}
        onClose={closePalette}
        navGroups={navGroups}
        navigate={handlePaletteNavigate}
      />

      {/* Right side controls */}
      <div className="flex items-center gap-2 md:gap-3">
        {/* Arama: yalnız ikon + kısayol rozeti. Paletin kendisi ortada açılır. */}
        <button
          ref={searchBtnRef}
          type="button"
          aria-label={t("topbar.search.aria")}
          aria-haspopup="dialog"
          aria-expanded={paletteOpen}
          onClick={openPalette}
          className={cn(
            "inline-flex h-8 shrink-0 items-center gap-2 rounded-lg border border-border px-2 text-muted-foreground transition-[background-color,border-color,color] duration-150",
            "hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            paletteOpen && "bg-accent text-foreground",
          )}
        >
          <Search size={16} aria-hidden="true" />
          <kbd className="max-md:hidden inline-flex select-none items-center rounded border border-border bg-muted px-1.5 py-0.5 text-[0.625rem] font-medium text-muted-foreground">
            {t("topbar.search.hint")}
          </kbd>
        </button>

        {showCurrencySwitcher && <CurrencySwitcher />}

        {showNotifications && (
          <Popover open={notifOpen} onOpenChange={setNotifOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={t("notification.title")}
                className={cn(
                  "relative inline-flex items-center justify-center rounded-lg p-1.5 transition-[background-color,border-color,color,transform] duration-150 ease-out-strong motion-safe:active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring border",
                  notifOpen
                    ? "bg-primary/8 border-primary/25 text-primary"
                    : notifUnread > 0
                      ? "bg-transparent border-border text-primary"
                      : "bg-transparent border-border text-muted-foreground hover:bg-accent",
                )}
              >
                <Bell size={18} aria-hidden="true" />
                {notifUnread > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-destructive text-[0.625rem] font-bold text-destructive-foreground flex items-center justify-center border-2 border-card leading-none">
                    {notifUnread > 99 ? "99+" : notifUnread}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              sideOffset={8}
              className="w-80 max-h-120 p-0 rounded-xl shadow-xl bg-popover flex flex-col overflow-hidden"
            >
              <div className="flex items-center justify-between px-3 py-2.5 border-b border-border shrink-0">
                <span className="text-sm font-semibold text-foreground">
                  {t("notification.title")}
                </span>
                {notifUnread > 0 && (
                  <button
                    type="button"
                    onClick={handleMarkAllRead}
                    className="text-xs text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                  >
                    {t("notification.markAllRead")}
                  </button>
                )}
              </div>

              <div className="flex-1 overflow-y-auto min-h-0">
                {notifLoading && notifItems.length === 0 && (
                  <p className="py-5 text-center text-[0.8125rem] text-muted-foreground">
                    {t("notification.loading")}
                  </p>
                )}
                {notifError && (
                  <p className="py-5 text-center text-[0.8125rem] text-destructive">
                    {t("notification.error")}
                  </p>
                )}
                {!notifLoading && !notifError && notifItems.length === 0 && (
                  <p className="py-6 text-center text-[0.8125rem] text-muted-foreground">
                    {t("notification.empty")}
                  </p>
                )}
                {notifItems.map((n) => {
                  const notifBody = renderNotificationBody(n);
                  return (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => handleNotifClick(n)}
                      className={cn(
                        "w-full text-left flex gap-2.5 items-start px-3 py-2.5 border-b border-border/50 last:border-b-0 transition-colors",
                        n.isRead
                          ? "bg-popover hover:bg-accent"
                          : "bg-primary/5 hover:bg-primary/10",
                      )}
                    >
                      <span
                        className={cn(
                          "mt-1.5 shrink-0 size-2 rounded-full",
                          n.isRead ? "bg-transparent" : "bg-primary",
                        )}
                      />
                      <span className="flex-1 min-w-0">
                        <span
                          className={cn(
                            "block text-[0.8125rem] text-foreground truncate",
                            n.isRead ? "font-medium" : "font-semibold",
                          )}
                        >
                          {renderNotificationTitle(n)}
                        </span>
                        {notifBody && (
                          <span className="block text-xs text-muted-foreground truncate mt-0.5">
                            {notifBody}
                          </span>
                        )}
                        <span className="block text-[0.6875rem] text-muted-foreground mt-1">
                          {formatTimeAgo(
                            n.createdAt,
                            t as (
                              k: string,
                              opts?: Record<string, unknown>,
                            ) => string,
                            formatDate,
                          )}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        )}

        <UserMenu
          profileDropdownItems={profileDropdownItems}
          handleLogout={handleLogout}
          loggingOut={loggingOut}
          logoutError={logoutError}
          onNavigate={onNavigate}
        />
      </div>
    </div>
  );
});

// ── AppShell ──────────────────────────────────────────────────────────────────

export interface AppShellConfig {
  navGroups: NavGroup[];
  badge: AppShellBadge;
  /** isActive predicate for nav items. Caller controls matching strategy. */
  isActive: (path: string) => boolean;
  /** Items shown above "logout" in the sidebar footer dropdown.
   *  Empty array = logout-only (super-admin). */
  profileDropdownItems: ProfileDropdownItem[];
  showNotifications: boolean;
  showCurrencySwitcher: boolean;
  tenantId?: string;
  handleLogout: () => Promise<void>;
  loggingOut: boolean;
  logoutError: string | null;
  /** Where the route error screen's "Home" action leads. */
  homePath: string;
  /** Called when the command palette or notification link triggers navigation. */
  onNavigate: (path: string) => void;
}

interface AppShellProps extends AppShellConfig {
  children: React.ReactNode;
}

/**
 * Full application shell: SidebarProvider + sidebar + topbar + scroll container.
 * Renders `children` inside the single `#main` scroll container.
 *
 * TenantLayout and AdminLayout are thin wrappers that pass their config here.
 */
export const AppShell: React.FC<AppShellProps> = ({
  navGroups,
  badge,
  isActive,
  profileDropdownItems,
  showNotifications,
  showCurrencySwitcher,
  tenantId,
  handleLogout,
  loggingOut,
  logoutError,
  onNavigate,
  homePath,
  children,
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [bulkJobDrawerId, setBulkJobDrawerId] = useState<string | null>(null);

  // shadcn SidebarProvider rail (open/closed) state'ini cookie'ye yazar ama
  // mount'ta okumaz (Next.js SSR senaryosu için tasarlanmış). Vite client-only
  // ortamda manuel olarak cookie'yi okuyup defaultOpen'a aktarıyoruz.
  const initialSidebarOpen = useMemo(() => {
    if (typeof document === "undefined") return true;
    const match = document.cookie.match(new RegExp("(?:^|;\\s*)" + SIDEBAR_COOKIE_NAME + "=([^;]+)"));
    return match ? match[1] !== "false" : true;
  }, []);

  // Ayarlar kendi ikincil sidebar'ını getiriyor. İki tam genişlikte kenar çubuğu
  // yan yana içerik alanını yiyor; bu yüzden ayarlara GİRERKEN ana sidebar bir kez
  // ray moduna çekiliyor, ayarlardan ÇIKARKEN önceki hâline dönüyor. "Bir kez":
  // kullanıcı ayarların içindeyken ana sidebar'ı yine de açabilir, karar ondan
  // geri alınmıyor; `preferredOpen` ayarların dışındaki son tercihi taşır.
  const inSettings = pathname.startsWith(SETTINGS_ROUTES.root);
  const [sidebarState, setSidebarState] = useState(() => ({
    inSettings,
    open: inSettings ? false : initialSidebarOpen,
    preferredOpen: initialSidebarOpen,
  }));

  // Geçiş render sırasında türetiliyor (React'in "prop değişince state'i ayarla"
  // kalıbı). Bir effect + setState burada fazladan bir kaskad render üretirdi.
  if (sidebarState.inSettings !== inSettings) {
    setSidebarState((prev) => ({
      inSettings,
      open: inSettings ? false : prev.preferredOpen,
      preferredOpen: inSettings ? prev.open : prev.preferredOpen,
    }));
  }

  const handleSidebarOpenChange = useCallback((next: boolean) => {
    setSidebarState((prev) => ({
      ...prev,
      open: next,
      preferredOpen: prev.inSettings ? prev.preferredOpen : next,
    }));
  }, []);

  return (
    <SidebarProvider open={sidebarState.open} onOpenChange={handleSidebarOpenChange}>
      <nav aria-label={t("common.a11y.skipNav")}>
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-60 focus:bg-background focus:px-3 focus:py-2 focus:rounded focus:shadow"
        >
          {t("common.a11y.skipToContent")}
        </a>
      </nav>

      <AppShellSidebar
        navGroups={navGroups}
        badge={badge}
        isActive={isActive}
        navigate={onNavigate}
      />

      {/* Single-scroll shell: the inset is viewport-height, the topbar is a fixed
          flex header, and #main is the ONE scroll container. This lets in-page
          sticky headers (e.g. the products table) resolve to #main without a
          second nested scrollbar. */}
      <SidebarInset className="h-svh overflow-hidden">
        <AppShellTopbar
          navGroups={navGroups}
          showNotifications={showNotifications}
          showCurrencySwitcher={showCurrencySwitcher}
          tenantId={tenantId}
          onNavigate={onNavigate}
          onBulkJobOpen={setBulkJobDrawerId}
          profileDropdownItems={profileDropdownItems}
          handleLogout={handleLogout}
          loggingOut={loggingOut}
          logoutError={logoutError}
        />

        {/* Ayarlar kabuğu kendi ikincil sidebar'ını tam yükseklikte konumlandırdığı
            için sayfa dolgusunu kendisi veriyor; burada dolgu bırakmak rail'i
            topbar'dan bir dolgu boyu aşağı iterdi (sticky offset padding-box'a
            göre hesaplanır). */}
        <div
          id="main"
          tabIndex={-1}
          className={cn(
            "flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden",
            !inSettings && "p-4 md:p-8",
          )}
        >
          <RouteErrorBoundary homePath={homePath}>
            {children}
          </RouteErrorBoundary>
        </div>

        {showNotifications && (
          <JobResultDrawer
            open={!!bulkJobDrawerId}
            onClose={() => setBulkJobDrawerId(null)}
            tenantId={tenantId ?? ""}
            jobId={bulkJobDrawerId}
          />
        )}
      </SidebarInset>
    </SidebarProvider>
  );
};
